import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const resourceIcons = ["ri-table-line", "ri-price-tag-3-line", "ri-user-search-line", "ri-truck-line", "ri-shield-check-line", "ri-lock-line"];
const resourceKeys = ["spreadsheet", "productLists", "sellerGuides", "shippingGuides", "qcResources", "safetyGuides"];

const popularIcons = ["ri-star-line", "ri-money-dollar-circle-line", "ri-coupon-2-line", "ri-percent-line", "ri-store-2-line", "ri-shield-check-line"];
const popularKeys = ["review", "shippingCost", "coupon", "discountCode", "warehouse", "legit"];
const popularImages = [
  "https://readdy.ai/api/search-image?query=A%20white%20sneaker%20displayed%20beside%20a%20row%20of%20five%20golden%20star%20rating%20icons%20on%20a%20soft%20cream%20background%2C%20bright%20studio%20light%2C%20customer%20review%20concept%2C%20clean%20and%20modern&width=600&height=450&seq=popular-review-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=A%20kraft%20cardboard%20shipping%20box%20with%20a%20small%20price%20tag%20attached%20beside%20a%20folded%20shirt%20on%20a%20warm%20beige%20surface%2C%20soft%20natural%20light%2C%20shipping%20cost%20concept%2C%20clean%20and%20tidy&width=600&height=450&seq=popular-shipping-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=A%20cheerful%20discount%20coupon%20ticket%20with%20a%20scissors%20line%20and%20percentage%20symbol%20beside%20a%20small%20shopping%20bag%20on%20a%20soft%20peach%20background%2C%20bright%20studio%20light%2C%20savings%20concept%2C%20fresh%20and%20inviting&width=600&height=450&seq=popular-coupon-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=A%20rounded%20gift%20tag%20labeled%20with%20a%20promotional%20code%20beside%20a%20folded%20sweater%20on%20a%20mint%20background%2C%20bright%20even%20light%2C%20discount%20code%20concept%2C%20clean%20and%20friendly&width=600&height=450&seq=popular-discount-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=A%20bright%20warehouse%20shelf%20neatly%20stacked%20with%20labeled%20cardboard%20boxes%2C%20soft%20overhead%20light%2C%20clean%20logistics%20and%20storage%20concept%2C%20minimal%20and%20modern&width=600&height=450&seq=popular-warehouse-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=A%20green%20shield%20checkmark%20badge%20floating%20beside%20a%20folded%20t-shirt%20and%20a%20small%20package%20on%20a%20light%20background%2C%20soft%20studio%20light%2C%20trust%20and%20safety%20concept%2C%20clean%20and%20reassuring&width=600&height=450&seq=popular-legit-01&orientation=landscape",
];

export function ResourceGrid() {
  const { t } = useTranslation();

  return (
    <section id="resources" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-2xl font-bold text-foreground-950 md:text-4xl">{t("resources.title")}</h2>
        <p className="mt-3 text-base leading-relaxed text-foreground-600">{t("resources.subtitle")}</p>
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {resourceKeys.map((key, i) => (
          <div key={key} className="rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className={`${resourceIcons[i]} text-xl`} />
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{t(`resources.cards.${key}.title`)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(`resources.cards.${key}.desc`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PopularGrid() {
  const { t } = useTranslation();

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-4xl">{t("popular.title")}</h2>
          <p className="mt-3 text-base text-foreground-600">{t("popular.subtitle")}</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {popularKeys.map((key, i) => (
            <div key={key} className="overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300">
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img src={popularImages[i]} alt={t(`popular.cards.${key}.title`)} className="h-full w-full object-cover object-top" />
              </div>
              <div className="flex items-start gap-3 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${popularIcons[i]} text-lg`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{t(`popular.cards.${key}.title`)}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{t(`popular.cards.${key}.desc`)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface HighlightSectionProps {
  id?: string;
  icon: string;
  title: string;
  text: string;
  ctaLabel?: string;
  ctaTo?: string;
  alt?: boolean;
}

export function HighlightSection({ id, icon, title, text, ctaLabel, ctaTo, alt }: HighlightSectionProps) {
  return (
    <section id={id} className={alt ? "bg-background-100" : ""}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="mx-auto max-w-3xl text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className={`${icon} text-xl`} />
          </span>
          <h2 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h2>
          <p className="mt-3 text-base leading-relaxed text-foreground-600">{text}</p>
          {ctaLabel && ctaTo && (
            <Link
              to={ctaTo}
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {ctaLabel}
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}